import React, { useEffect } from 'react';
import { useTranslation } from 'localization';
import { Skeleton } from 'primereact/skeleton';
import { Sphere } from 'types/global';

import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import PeriodCardAtom, { PeriodCardData } from '../../atoms/PeriodCard/PeriodCardAtom';
import { LevelPillsAtom } from '../../atoms/LevelPills';
import { DateNavigatorAtom } from '../../atoms/DateNavigator';
import { TimeframeNestingLevel } from '../PeriodSelector/periodSelectorUtils';
import { usePeriodGrid } from './usePeriodGrid';

import './PeriodGridMolecule.scss';

export interface PeriodGridMoleculeProps {
  /** Root timeframe ID for fetching virtual periods */
  timeframeId?: string;
  /** Display name shown in the header */
  timeframeName?: string;
  /** Nested timeframe structure for level extraction */
  nestedTimeframes?: TimeframeNestingLevel[];
  /** Schedule definition for level inference on flat timeframes */
  scheduleDefinition?: { rrule?: string; type?: string } | null;
  /** Keys of currently selected periods (drives checkbox state) */
  selectedKeys: Set<string>;
  /** Fires when a period card is toggled */
  onToggle: (period: PeriodCardData | Sphere) => void;
  /** External search term for client-side period name filtering */
  searchTerm?: string;
  /** Called when an API fetch error occurs */
  onError?: (messageKey: string) => void;
  className?: string;
}

const SKELETON_COUNT = 12;

const PeriodGridMolecule: React.FC<PeriodGridMoleculeProps> = ({
  timeframeId,
  timeframeName,
  nestedTimeframes,
  scheduleDefinition,
  selectedKeys,
  onToggle,
  searchTerm = '',
  onError,
  className = '',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const {
    levels,
    activeLevel,
    windowLabel,
    filtered,
    loading,
    error,
    handleLevelChange,
    handlePrev,
    handleNext,
    handleNow,
  } = usePeriodGrid({
    graphClient: apiClients?.graphClient,
    timeframeId,
    nestedTimeframes,
    scheduleDefinition,
    searchTerm,
  });

  useEffect(() => {
    if (error && onError) onError(error);
  }, [error, onError]);

  if (!timeframeId) return null;

  return (
    <div className={className ? `period-grid ${className}` : 'period-grid'}>
      {/* Header: title + level pills + date navigator */}
      <div className="period-grid__header">
        {timeframeName && (
          <h3 className="period-grid__title">{timeframeName}</h3>
        )}
        <LevelPillsAtom
          levels={levels}
          activeLevel={activeLevel}
          onChange={handleLevelChange}
        />
        <DateNavigatorAtom
          windowLabel={windowLabel}
          onPrev={handlePrev}
          onNext={handleNext}
          onNow={handleNow}
        />
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="period-grid__cards period-grid__cards--skeleton">
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <Skeleton key={i} height="5rem" className="period-grid__skeleton-card" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="period-grid__empty">
          <span>
            {searchTerm.trim()
              ? t('periodSelector.noMatchingPeriods')
              : t('periodSelector.emptyState')}
          </span>
        </div>
      )}

      {/* Period card grid */}
      {!loading && filtered.length > 0 && (
        <div className="period-grid__cards">
          {filtered.map((period) => (
            <PeriodCardAtom
              key={period.key}
              period={period}
              chosen={selectedKeys.has(period.key)}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PeriodGridMolecule;
